/**
 * Isometric line drawings for the lead generation process (Drömkund och
 * erbjudande, Listor och signaler, Personliga sekvenser, Möten och
 * förbättring). Same projection, frame, stroke and animation hooks as the
 * other service drawings, with their own motifs and motion:
 * - 01 a town of small houses; the few that fit the ideal customer light up
 * - 02 a signal mast pulses while the accounts it picks up are linked to it
 * - 03 a sealed letter travels a stepped route of touch points
 * - 04 a meeting table with two seats; a check is drawn on the calendar board
 */

import type { CSSProperties } from "react";

import {
  box,
  CORNER_CROSSES,
  floorGrid,
  IllustrationFrame,
  motionPath,
  polyline,
  project,
  type Point3,
  type ProcessIllustration,
  type Shape,
} from "@/components/services/seo/seo-process-illustrations";

function dotAt(point: Point3) {
  const [cx, cy] = project(point);
  return { cx, cy };
}

/** A flat rectangle on a vertical face at depth y (spans x and z). */
function faceRect(x: number, y: number, z: number, w: number, h: number) {
  return polyline(
    [
      [x, y, z],
      [x + w, y, z],
      [x + w, y, z + h],
      [x, y, z + h],
    ],
    true,
  );
}

/** A small house: a box with a ridge line on top. */
function house(x: number, y: number, w = 8, d = 8, h = 7): Shape[] {
  return [
    ...box(x, y, 4, w, d, h),
    { d: polyline([[x, y + d / 2, 4 + h + 4], [x + w, y + d / 2, 4 + h + 4]]) },
    { d: polyline([[x, y, 4 + h], [x, y + d / 2, 4 + h + 4], [x, y + d, 4 + h]]) },
  ];
}

// ---------------------------------------------------------------- 01 Drömkund och erbjudande

const TOWN: [number, number][] = [
  [10, 10], [30, 12], [52, 8], [74, 12],
  [12, 34], [34, 32], [56, 36], [78, 32],
  [10, 58], [32, 56], [54, 60], [76, 56],
  [20, 78], [44, 80], [68, 78],
];
/** Index of the houses that match the ideal customer. */
const FIT = [5, 7, 13];

function IdealCustomerIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [...box(0, 0, 0, 96, 96, 4), ...TOWN.flatMap(([x, y]) => house(x, y))];
  const construct = floorGrid(100, 20).filter((_, index) => index % 2 === 0);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {FIT.map((index, order) => {
        const [x, y] = TOWN[index];
        return (
          <circle
            key={index}
            className="cc-ill-lead-fit__node"
            style={{ "--cc-ill-node": order } as CSSProperties}
            {...dotAt([x + 4, y + 4, 22])}
            r={3}
          />
        );
      })}
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Listor och signaler

const MAST: Point3 = [46, 46, 4];
const MAST_TOP: Point3 = [50, 50, 48];
const LISTENERS: [number, number][] = [
  [12, 16],
  [78, 14],
  [14, 76],
  [80, 74],
];

function SignalsIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 0, 0, 96, 96, 4),
    ...box(MAST[0], MAST[1], 4, 8, 8, 6),
    ...box(MAST[0] + 2, MAST[1] + 2, 10, 4, 4, 38),
    ...LISTENERS.flatMap(([x, y]) => house(x, y)),
  ];
  const construct = [
    polyline([[0, 50, 4], [96, 50, 4]]),
    polyline([[50, 0, 4], [50, 96, 4]]),
  ];
  const top = project(MAST_TOP);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <g className="cc-ill-lead-signal__links">
        {LISTENERS.map(([x, y], index) => (
          <path
            key={index}
            d={polyline([MAST_TOP, [x + 4, y + 4, 19]])}
            pathLength={1}
            style={{ "--cc-ill-segment": index } as CSSProperties}
          />
        ))}
      </g>
      <circle className="cc-ill-lead-signal__pulse" cx={top[0]} cy={top[1]} r={3} />
      <circle
        className="cc-ill-lead-signal__pulse cc-ill-lead-signal__pulse--late"
        cx={top[0]}
        cy={top[1]}
        r={3}
      />
      <circle className="cc-ill__dot" cx={top[0]} cy={top[1]} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 03 Personliga sekvenser

/** Touch points along the route: profile visit, request, email, video, message. */
const STONES: Point3[] = [
  [10, 70, 6],
  [28, 58, 6],
  [46, 66, 6],
  [64, 50, 6],
  [82, 40, 6],
];

function SequenceIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 20, 0, 96, 64, 4),
    ...STONES.map(([x, y]) => box(x - 5, y - 5, 4, 10, 10, 2)).flat(),
  ];
  const construct = [polyline([[0, 52, 4], [96, 52, 4]])];
  const [endX, endY] = project(STONES[STONES.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-lead-seq__route" d={polyline(STONES)} pathLength={1} />
      <g className="cc-ill-lead-seq__letter" transform={`translate(${endX.toFixed(1)} ${endY.toFixed(1)})`}>
        <rect x={-6} y={-4.5} width={12} height={9} rx={1} />
        <path d="M-6 -4.5 L0 0.5 L6 -4.5" />
        <animateMotion
          begin="indefinite"
          dur="2s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(STONES)}
        />
      </g>
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Möten och förbättring

const BOARD_Y = 30;
const CHECK: Point3[] = [
  [40, BOARD_Y, 44],
  [46, BOARD_Y, 38],
  [60, BOARD_Y, 52],
];

function MeetingIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 20, 0, 96, 64, 4),
    // calendar board on two legs
    ...box(28, BOARD_Y - 4, 4, 4, 4, 30),
    ...box(68, BOARD_Y - 4, 4, 4, 4, 30),
    ...box(26, BOARD_Y - 2, 26, 48, 2, 34),
    { d: faceRect(30, BOARD_Y, 30, 40, 26) },
    // table and two seats
    ...box(36, 52, 4, 28, 16, 12),
    ...box(24, 56, 4, 8, 8, 7),
    ...box(68, 56, 4, 8, 8, 7),
  ];
  const construct = [
    ...[36, 42, 48].map((z) => polyline([[30, BOARD_Y, z], [70, BOARD_Y, z]])),
    ...[40, 50, 60].map((x) => polyline([[x, BOARD_Y, 30], [x, BOARD_Y, 56]])),
  ];

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-lead-meet__check" d={polyline(CHECK)} pathLength={1} />
      <circle className="cc-ill__dot" {...dotAt([50, 60, 16])} r={2.6} />
    </IllustrationFrame>
  );
}

export const LEAD_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  IdealCustomerIllustration,
  SignalsIllustration,
  SequenceIllustration,
  MeetingIllustration,
];
